
import React, { ChangeEvent, useEffect, useState } from 'react';
import moment from 'moment';

import { Form, Input, Button, Row, Col, Card, Select, Collapse, TablePaginationConfig, DatePicker, Tooltip } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { bookingManualSliceActions, getBookingManual, IBookingManualState, postBookingManual, updateBookingManual } from '../../../../../share/store/slices/booking-manual';
import { DownOutlined, UpOutlined } from '@ant-design/icons';
import { ILoginState, IMenuState } from '@share/store/slices';
import { FunctionalRootState, Toaster } from '@share/utils';
import { FormattedMessage, useIntl } from 'react-intl';
import { IFiltersManualBooking } from '../component';

import { isEmpty } from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle, faSearch } from '@fortawesome/free-solid-svg-icons';
import { BookingManualAPI } from '@share/api';
import { Link } from 'react-router-dom';

import './style.scss';

const { Option } = Select;

const OPTION1 = 'This is a new booking that I’m entering for the first time">This is a new booking that I’m entering for the first time';
const OPTION2 = 'This a change to a booking previously entered (make sure to enter the same booking # previously entered)';
const OPTION3 = 'This is a cancellation of booking previously entered (make sure to enter the same booking # previously entered)';

interface IProps {
  bookingManualStore: IBookingManualState;
  pagination: TablePaginationConfig;
  agentID: number;
  filters: IFiltersManualBooking;
  sort: any;
}

const ManualBookingForm: React.FC<IProps> = (props: IProps) => {
  const { bookingManualStore, pagination, agentID, filters, sort } = props;
  const { postBookingManualError, postBookingManual: postBookingManualRes } = bookingManualStore;

  const [collapsed, setCollapsed] = useState(true);
  const [file, setFile] = useState<File | null>(null);
  const [bookingType, setBookingType] = useState<string | null>(null);
  const [bookingSelectionOption, setBookingSelectionOption] = useState<string | null>(null);
  const [bookingSelected, setBookingSelected] = useState<string>('');
  const [bookingSelectedObject, setBookingSelectedObject] = useState<any>(null);

  const { items }: IMenuState = useSelector((state: FunctionalRootState) => state.navigationMenuStore);
  const { user, account }: ILoginState = useSelector((state: FunctionalRootState) => state.loginStore);
  const { postBookingManualLoading }: IBookingManualState = useSelector((state: FunctionalRootState) => state.bookingManualStore);

  const isOption1 = bookingSelectionOption === OPTION1;
  const isOption2 = bookingSelectionOption === OPTION2;
  const isOption3 = bookingSelectionOption === OPTION3;

  const [form] = Form.useForm();

  const dispatch = useDispatch();

  const intl = useIntl();
  
  const handleFile = (e: ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files;
    if (selectedFile) {
      setFile(selectedFile[0]);
    }
  };

  const toggleCollapse = () => {
    setCollapsed(!collapsed);

    setBookingSelectionOption(null);
    setBookingSelected('');
    setBookingSelectedObject(null);

    form?.resetFields();
  };

  const handleSubmit = (values: any) => {
    const formData = new FormData();
  
    // Format dates before appending
    const formattedValues = {
      ...values,
      travelDateStart: values.travelDateStart ? values.travelDateStart.format('YYYY-MM-DD') : null,
      travelDateEnd: values.travelDateEnd ? values.travelDateEnd.format('YYYY-MM-DD') : null,
      bookingDate: values.bookingDate ? values.bookingDate.format('YYYY-MM-DD') : null,
      dateOfSubmission: values.dateOfSubmission ? values.dateOfSubmission.format('YYYY-MM-DD') : null,
      numberOfTravelers: (!values.numberOfTravelers || values.numberOfTravelers === 'undefined' || isEmpty(values.numberOfTravelers)) ? undefined : values.numberOfTravelers,
    };

    Object.entries(formattedValues).forEach(([key, value]) => {
      if (key !== 'file' && value !== null) {
        if (key !== 'numberOfTravelers' || (values.numberOfTravelers && values.numberOfTravelers !== 'undefined' && !isEmpty(values.numberOfTravelers))) {
          formData.append(key, value as string);
        }
      }
    });

    if (file) {
      formData.append('file', file);
    }

    if (bookingSelectionOption === OPTION1) {
      formData.append('refundAdjustment', 'NewBooking');
      dispatch(postBookingManual(formData, handleSubmitSuccess));
    } else if (bookingSelectionOption && bookingSelectedObject && [OPTION2, OPTION3].includes(bookingSelectionOption)) {
      const formattedData = {
        ...bookingSelectedObject,

        //-----------------------------------------------
        bookingId: values.bookingId,
        bookingDate: values.bookingDate ? values.bookingDate.format('YYYY-MM-DD') : null,
        travelDateStart: values.travelDateStart ? values.travelDateStart.format('YYYY-MM-DD') : null,
        travelDateEnd: values.travelDateEnd ? values.travelDateEnd.format('YYYY-MM-DD') : null,
        bookingType: values.bookingType,
        clientName: values.clientName,
        serviceProviderName: values.serviceProviderName,
        serviceConfirmationNumber: values.serviceConfirmationNumber,
        numberOfTravelers: values.numberOfTravelers,
        detailsOfServices: values.detailsOfServices,
        totalBookingAmount: values.totalBookingAmount,
        totalCommission: values.totalCommission,
        refundsOrAdjustments: values.refundsOrAdjustments,
        dateOfSubmission: values.dateOfSubmission ? values.dateOfSubmission.format('YYYY-MM-DD') : null,
        refundAdjustment: isOption1 ? 0 : isOption2 ? 1 : 2
      };

      const formData = new FormData();
      Object.entries(formattedData).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          formData.append(key, String(value));
        }
      });
  
      dispatch(updateBookingManual(formData, handleSubmitSuccess));
    }
  };

  const handleSubmitSuccess = () => {
    dispatch(getBookingManual(pagination, agentID, sort, filters));

    setBookingSelectionOption(null);
    setBookingSelected('');
    setBookingSelectedObject(null);
  };

  const handleNumberChange = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const key = e.key;

    if (
      !/^([a-zA-Z0-9. ]+)$/.test(key) && // Allow only numbers
      key !== 'Backspace' && // Allows delete numbers
      key !== 'Tab' && // Allows tab
      key !== 'ArrowLeft' && // Allows left arrow
      key !== 'ArrowRight' && // Allows right arrow
      key !== 'Delete' && // Allows delete key
      !(e.ctrlKey && (key === 'c' || key === 'v' || key === 'x' || key === 'a')) // Allows copy (Ctrl+C), paste (Ctrl+V), cut (Ctrl+X), and select all (Ctrl+A)
    ) {
      e.preventDefault();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const clipboardData = e.clipboardData.getData('text');
    if (!/^\d*\.?\d*$/.test(clipboardData)) {
      // Only allow numeric values with optional decimal point
      e.preventDefault();
    }
  };

  const handleReset = () => {
    form.resetFields();
  };

  useEffect(() => {
    if (
      !postBookingManualLoading &&
      postBookingManualRes?.success
    ) {
      Toaster.success('Booking manual submitted successfully!');
      handleReset(); // Clean the form
      setCollapsed(true);
      dispatch(bookingManualSliceActions.setPostManualBookingError(''));
      dispatch(bookingManualSliceActions.setPostManualBooking(null));
    } else if (postBookingManualError) {
      Toaster.error(postBookingManualError);
      dispatch(bookingManualSliceActions.setPostManualBookingError(''));
    }
  }, [postBookingManual, postBookingManualLoading]);

  const { formatMessage } = useIntl();

  const handleSearchBooking = async () => {
    if (!isEmpty(bookingSelected)) {
      try {
        dispatch(bookingManualSliceActions.setPostManualBookingLoading(true));

        const { data } = await BookingManualAPI.get({ current: 1, pageSize: 1, total: 1 }, agentID, sort, { manualBookingId: +bookingSelected } as IFiltersManualBooking);

        if (data?.isNotEmpty) {
          const booking = data?.items[0];

          if (booking?.manualBookingId === +bookingSelected) {
            if (booking?.isPaid) {
              Toaster.error(intl.formatMessage({ id: 'bookingmanual.booking.search.paid' }));
            } else {
              setBookingSelectedObject(booking);
              form.setFieldsValue({
                dateOfSubmission: booking?.dateOfSubmission ? moment(booking?.dateOfSubmission, 'yyyy-MM-DD') : null,
                bookingDate: booking?.bookingDate ? moment(booking?.bookingDate, 'yyyy-MM-DD') : null,
                bookingId: booking?.bookingId,
                bookingType: booking?.bookingType,
                clientName: booking?.clientName,
                detailsOfServices: booking?.detailsOfServices,
                numberOfTravelers: booking?.numberOfTravelers,
                pathFile: booking?.pathFile,
                serviceConfirmationNumber: booking?.serviceConfirmationNumber,
                serviceProviderName: booking?.serviceProviderName,
                totalBookingAmount: booking?.totalBookingAmount,
                totalCommission: booking?.totalCommission,
                travelDateEnd: booking?.travelDateEnd ? moment(booking?.travelDateEnd, 'yyyy-MM-DD') : null,
                travelDateStart: booking?.travelDateStart ? moment(booking?.travelDateStart, 'yyyy-MM-DD') : null,
              });
            }
          } else {
            Toaster.error(intl.formatMessage({ id: 'bookingmanual.booking.search.no_available' }));
          }
        } else if (data?.isEmpty) {
          Toaster.error(intl.formatMessage({ id: 'bookingmanual.booking.search.no_available' }));
        }
        dispatch(bookingManualSliceActions.setPostManualBookingLoading(false));
      } catch (error) {
        dispatch(bookingManualSliceActions.setPostManualBookingLoading(false));
        console.error(error);
      }
    }
  };

  const displayBookingSelection = bookingSelectionOption === OPTION2 || bookingSelectionOption === OPTION3;

  const shouldDisableFieldsMain = (displayBookingSelection && !bookingSelectedObject) || isOption3;
  const shouldDisableFields = shouldDisableFieldsMain || isOption2;
  const shouldDisableEditableFields = shouldDisableFieldsMain;

  const missingFields = (isEmpty(items?.firstName) && isEmpty(items?.lastName)) || !items?.rsiId || isEmpty(user?.email);

  return (
    <div className="manual-booking-form" style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
      <Card
        bordered={true}
        style={{
          marginTop: '16px',
          borderRadius: '8px',
          boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
          width: '100%',
        }}
        className={collapsed ? 'collapsed-form' : ''}
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>{formatMessage({ id: 'bookingmanual.formTitle' })}</span>
            {!collapsed && (
              <Button type="primary" onClick={toggleCollapse} icon={<UpOutlined />} style={{ display: 'flex', alignItems: 'center', width: '200px' }}>
                {formatMessage({ id: 'bookingmanual.collapse' })}
              </Button>)}
            {collapsed && (
              <Button type="primary" onClick={() => setCollapsed(false)} icon={<DownOutlined />} style={{ display: 'flex', alignItems: 'center', width: '200px' }}>
                {formatMessage({ id: 'bookingmanual.addBookingManual' })}
              </Button>)}
          </div>
        }
      >
        {!collapsed && (
          <Collapse defaultActiveKey={['1']} bordered={false}>
            <Collapse.Panel key="1" header={null} showArrow={false}>
              <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
                initialValues={{
                  agentName: `${items?.firstName} ${items?.lastName}`,
                  agentID: `${items?.rsiId}`,
                  contactInformation: `${user?.email}`,
                }}
                style={{ padding: '20px' }}
                autoComplete="off"
              >
                {missingFields && (
                  <Row gutter={24}>
                    <Col span={24}>
                      <Form.Item>
                        <div className="ant-alert ant-alert-error" style={{ color: 'red' }}>
                          Agent Name, Agent ID, and Contact Information are required. To proceed go to <Link to={`/${account?.name}/account`} style={{ margin: '0 5px' }}>My Account</Link> to update your profile.
                        </div>
                      </Form.Item>
                    </Col>
                  </Row>)}

                <Row gutter={16}>
                  <Col span={16}>
                    <Form.Item
                      label={formatMessage({ id: 'bookingmanual.refundsOrAdjustments.new' })}
                      name="refundsOrAdjustments"
                      rules={[
                        {
                          required: true,
                          message: formatMessage({
                            id: 'bookingmanual.refundsOrAdjustments.error',
                          }),
                        },
                      ]}
                    >
                      <Select
                        placeholder={formatMessage({ id: 'bookingmanual.refundsOrAdjustments.placeholder' })}
                        value={bookingSelectionOption}
                        disabled={missingFields}
                        onChange={(v) => {
                          setBookingSelectionOption(v);
                          setBookingSelected('');
                          setBookingSelectedObject(null);

                          form.resetFields();
                          form.setFieldsValue({ refundsOrAdjustments: v });
                        }}
                      >
                        <Option value={OPTION1}>{OPTION1}</Option>
                        <Option value={OPTION2}>{OPTION2}</Option>
                        <Option value={OPTION3}>{OPTION3}</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                  {displayBookingSelection && (
                    <Col span={8}>
                      <Form.Item
                        label={formatMessage({ id: 'bookingmanual.bookingSelected.new' })}
                        name="bookingSelected"
                      >
                        <div className="booking-selected-input">
                          <Input 
                            placeholder={formatMessage({ id: 'bookingmanual.bookingSelected.placeholder' })} 
                            onChange={(e) => {
                              setBookingSelected(e.target.value);
                              setBookingSelectedObject(null);
                            }}
                            value={bookingSelected}
                            name="bookingSelected"
                            type="number"
                          />
                          <span>
                            <FontAwesomeIcon icon={faSearch} onClick={handleSearchBooking} /> 
                          </span>
                        </div>
                        {!bookingSelectedObject && (
                          <div style={{ display: 'flex', flexWrap: 'nowrap' }}>
                            <div id="dateOfSubmission_help" className="ant-form-item-explain ant-form-item-explain-connected" role="alert">
                              <div className="ant-form-item-explain-error">
                                {(!bookingSelected || isEmpty(bookingSelected)) ? (
                                    <FormattedMessage id="bookingmanual.bookingManualId.error" />) : (
                                    <FormattedMessage id="bookingmanual.bookingManualId.click.error" />)}
                              </div>
                            </div>
                            <div style={{ width: '0px', height: '24px' }}></div>
                          </div>)}
                      </Form.Item>
                    </Col>)}
                </Row>

                <Row gutter={16}>
                  <Col span={8}>
                    <Form.Item
                      label={formatMessage({ id: 'bookingmanual.agentName' })}
                      name="agentName"
                      rules={[
                        {
                          required: true,
                          message: formatMessage({ id: 'bookingmanual.agentName.error' }),
                        },
                      ]}
                    >
                      <Input
                        placeholder={formatMessage({ id: 'bookingmanual.agentName.placeholder' })}
                        disabled={true}
                        className="skip-disabled-input"
                      />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item
                      label={formatMessage({ id: 'bookingmanual.agentId' })}
                      name="agentID"
                      rules={[
                        {
                          required: true,
                          message: formatMessage({ id: 'bookingmanual.agentId.error' }),
                        },
                      ]}
                    >
                      <Input
                        placeholder={formatMessage({ id: 'bookingmanual.agentId.placeholder' })}
                        disabled={true}
                        className="skip-disabled-input"
                      />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item
                      label={formatMessage({ id: 'bookingmanual.contactInfo' })}
                      name="contactInformation"
                      rules={[
                        {
                          required: true,
                          message: formatMessage({ id: 'bookingmanual.contactInfo.error' }),
                        },
                      ]}
                    >
                      <Input
                        placeholder={formatMessage({ id: 'bookingmanual.contactInfo.placeholder' })}
                        disabled={true}
                        className="skip-disabled-input"
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col span={8}>
                    <Form.Item
                      label={formatMessage({ id: 'bookingmanual.bookingId' })}
                      name="bookingId"
                      rules={[
                        {
                          required: true,
                          message: formatMessage({ id: 'bookingmanual.bookingId.error' }),
                        },
                      ]}
                    >
                      <Input
                        placeholder={formatMessage({ id: 'bookingmanual.bookingId.placeholder' })}
                        onKeyDown={handleNumberChange}
                        onPaste={handlePaste}
                        disabled={shouldDisableEditableFields || postBookingManualLoading || !(bookingSelectionOption && !isEmpty(bookingSelectionOption))}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item
                      label={formatMessage({ id: 'bookingmanual.bookingDate' })}
                      name="bookingDate"
                      rules={[
                        {
                          required: true,
                          message: formatMessage({ id: 'bookingmanual.bookingDate.error' }),
                        },
                      ]}
                    >
                      <DatePicker
                        format="MM/DD/YYYY"
                        style={{ width: '100%' }}
                        disabled={shouldDisableEditableFields || postBookingManualLoading || !(bookingSelectionOption && !isEmpty(bookingSelectionOption))}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item
                      label={formatMessage({ id: 'bookingmanual.bookingType' })}
                      name="bookingType"
                      rules={[
                        {
                          required: true,
                          message: formatMessage({ id: 'bookingmanual.bookingType.error' }),
                        },
                      ]}
                    >
                      <Select
                        placeholder={formatMessage({ id: 'bookingmanual.bookingType.placeholder' })}
                        onChange={(v) => setBookingType(v)}
                        disabled={shouldDisableEditableFields || postBookingManualLoading || !(bookingSelectionOption && !isEmpty(bookingSelectionOption))}
                      >
                        <Option value="Hotel">Hotel</Option>
                        <Option value="Condo">Condo</Option>
                        <Option value="Car">Car</Option>
                        <Option value="Flight">Flight</Option>
                        <Option value="Cruise">Cruise</Option>
                        <Option value="Vacation Package">Vacation Package</Option>
                        <Option value="Other">Other</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col span={8}>
                    <Form.Item
                      label={formatMessage({ id: 'bookingmanual.travelDateStart' })}
                      name="travelDateStart"
                      dependencies={['travelDateEnd']}
                      rules={[
                        {
                          required: true,
                          message: formatMessage({ id: 'bookingmanual.travelDateStart.error' }),
                        },
                        ({ getFieldValue }) => ({
                          validator(_, value) {
                            const endDate = getFieldValue('travelDateEnd');
                            if (!value || (endDate && value.isAfter(endDate, 'day'))) {
                              return Promise.reject(
                                new Error(formatMessage({ id: 'bookingmanual.travelStartDate.invalid' }))
                              );
                            }
                            return Promise.resolve();
                          },
                        }),
                      ]}
                    >
                      <DatePicker
                        format="MM/DD/YYYY"
                        style={{ width: '100%' }}
                        onChange={() => form.validateFields(['travelDateStart', 'travelDateEnd'])} // Revalidate both fields
                        disabled={shouldDisableEditableFields || postBookingManualLoading || !(bookingSelectionOption && !isEmpty(bookingSelectionOption))}
                      />
                    </Form.Item>
                  </Col>

                  <Col span={8}>
                    <Form.Item
                      label={formatMessage({ id: 'bookingmanual.travelEndDate' })}
                      name="travelDateEnd"
                      dependencies={['travelDateStart']}
                      rules={[
                        {
                          required: true,
                          message: formatMessage({ id: 'bookingmanual.travelEndDate.error' }),
                        },
                        ({ getFieldValue }) => ({
                          validator(_, value) {
                            const startDate = getFieldValue('travelDateStart');
                            if (!value || value.isSameOrAfter(startDate, 'day')) {
                              return Promise.resolve();
                            }
                            return Promise.reject(
                              new Error(formatMessage({ id: 'bookingmanual.travelEndDate.invalid' }))
                            );
                          },
                        }),
                      ]}
                    >
                      <DatePicker
                        format="MM/DD/YYYY"
                        style={{ width: '100%' }}
                        onChange={() => form.validateFields(['travelDateStart', 'travelDateEnd'])} // Revalidate both fields
                        disabled={shouldDisableEditableFields || postBookingManualLoading || !(bookingSelectionOption && !isEmpty(bookingSelectionOption))}
                      />
                    </Form.Item>
                  </Col>

                  <Col span={8}>
                    <Form.Item
                      label={formatMessage({ id: 'bookingmanual.dateOfSubmission' })}
                      name="dateOfSubmission"
                      rules={[
                        {
                          required: true,
                          message: formatMessage({ id: 'bookingmanual.dateOfSubmission.error' }),
                        },
                      ]}
                    >
                      <DatePicker
                        format="MM/DD/YYYY"
                        style={{ width: '100%' }}
                        disabled={shouldDisableEditableFields || postBookingManualLoading || !(bookingSelectionOption && !isEmpty(bookingSelectionOption))}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col span={8}>
                    <Form.Item
                      label={formatMessage({ id: 'bookingmanual.clientName' })}
                      name="clientName"
                      rules={[
                        {
                          required: true,
                          message: formatMessage({ id: 'bookingmanual.clientName.error' }),
                        },
                      ]}
                    >
                      <Input
                        placeholder={formatMessage({ id: 'bookingmanual.clientName.placeholder' })}
                        disabled={shouldDisableEditableFields || postBookingManualLoading || !(bookingSelectionOption && !isEmpty(bookingSelectionOption))}
                      />
                    </Form.Item>
                  </Col>

                  <Col span={8}>
                    <Form.Item
                      label={formatMessage({ id: 'bookingmanual.serviceProviderName' })}
                      name="serviceProviderName"
                      rules={[
                        {
                          required: true,
                          message: formatMessage({ id: 'bookingmanual.serviceProviderName.error' }),
                        },
                      ]}
                    >
                      <Input
                        placeholder={formatMessage({
                          id: 'bookingmanual.serviceProviderName.placeholder',
                        })}
                        disabled={shouldDisableEditableFields || postBookingManualLoading || !(bookingSelectionOption && !isEmpty(bookingSelectionOption))}
                      />
                    </Form.Item>
                  </Col>

                  <Col span={8}>
                    <Form.Item
                      label={formatMessage({ id: bookingType === 'Hotel' ? 'bookingmanual.numberOfRooms' : 'bookingmanual.numberOfTravelers' })}
                      name="numberOfTravelers"
                    >
                      <Input
                        type="number"
                        placeholder={formatMessage({
                          id: bookingType === 'Hotel' ? 'bookingmanual.numberOfRooms.placeholder' : 'bookingmanual.numberOfTravelers.placeholder',
                        })}
                        onKeyDown={handleNumberChange}
                        onPaste={handlePaste}
                        disabled={shouldDisableEditableFields || postBookingManualLoading || !(bookingSelectionOption && !isEmpty(bookingSelectionOption))}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col span={8}>
                    <Form.Item
                      label={formatMessage({ id: 'bookingmanual.totalCommission' })}
                      name="totalCommission"
                      rules={[
                        {
                          required: true,
                          message: formatMessage({ id: 'bookingmanual.totalCommission.error' }),
                        },
                      ]}
                    >
                      <Input
                        type="number"
                        placeholder={formatMessage({
                          id: 'bookingmanual.totalCommission.placeholder',
                        })}
                        onKeyDown={handleNumberChange}
                        onPaste={handlePaste}
                        disabled={shouldDisableEditableFields || postBookingManualLoading || !(bookingSelectionOption && !isEmpty(bookingSelectionOption))}
                      />
                    </Form.Item>
                  </Col>

                  <Col span={8}>
                    <Form.Item
                      label={formatMessage({ id: 'bookingmanual.detailsOfServices' })}
                      name="detailsOfServices"
                      rules={[
                        {
                          required: true,
                          message: formatMessage({ id: 'bookingmanual.detailsOfServices.error' }),
                        },
                      ]}
                    >
                      <Input
                        placeholder={formatMessage({
                          id: 'bookingmanual.detailsOfServices.placeholder',
                        })}
                        disabled={shouldDisableEditableFields || !(bookingSelectionOption && !isEmpty(bookingSelectionOption))}
                      />
                    </Form.Item>
                  </Col>

                  <Col span={8}>
                    <Form.Item
                      label={formatMessage({ id: 'bookingmanual.uploadFile' })}
                      name="file"
                      valuePropName="fileList"
                      rules={[
                        {
                          required: !shouldDisableFields,
                          message: formatMessage({ id: 'bookingmanual.uploadFile.error' }),
                        },
                      ]}
                    >
                      <Input 
                        name="file" 
                        type='file' 
                        onChange={handleFile} disabled={shouldDisableFields || postBookingManualLoading || !(bookingSelectionOption && !isEmpty(bookingSelectionOption))}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col span={16}>
                    <Form.Item
                      label={<label>{formatMessage({ id: 'bookingmanual.serviceConfirmationNumber.header' })}<Tooltip title={formatMessage({ id: 'bookingmanual.serviceConfirmationNumber.placeholder' })}><FontAwesomeIcon icon={faInfoCircle} style={{ marginLeft: '5px' }} /></Tooltip></label>}
                      name="serviceConfirmationNumber"
                      rules={[
                        {
                          required: true,
                          message: formatMessage({
                            id: 'bookingmanual.serviceConfirmationNumber.error',
                          }),
                        },
                      ]}
                    >
                      <Input
                        placeholder={formatMessage({
                          id: 'bookingmanual.serviceConfirmationNumber.placeholder',
                        })}
                        disabled={shouldDisableEditableFields || postBookingManualLoading || !(bookingSelectionOption && !isEmpty(bookingSelectionOption))}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item
                      label={formatMessage({ id: 'bookingmanual.totalBookingAmount' })}
                      name="totalBookingAmount"
                      rules={[
                        {
                          required: true,
                          message: formatMessage({ id: 'bookingmanual.totalBookingAmount.error' }),
                        },
                      ]}
                    >
                      <Input
                        type="number"
                        placeholder={formatMessage({
                          id: 'bookingmanual.totalBookingAmount.placeholder',
                        })}
                        onKeyDown={handleNumberChange}
                        onPaste={handlePaste}
                        disabled={shouldDisableEditableFields || postBookingManualLoading || !(bookingSelectionOption && !isEmpty(bookingSelectionOption))}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item style={{ marginBottom: '0px' }}>
                  <Button 
                    type="primary"
                    htmlType="submit"
                    disabled={postBookingManualLoading || !(bookingSelectionOption && !isEmpty(bookingSelectionOption)) || (!bookingSelectedObject && [OPTION2, OPTION3].includes(bookingSelectionOption)) || missingFields} 
                    style={{ width: '100%', marginTop: '15px' }}
                  >
                    {formatMessage({ id: 'bookingmanual.submit' })}
                  </Button>
                </Form.Item>
              </Form>
            </Collapse.Panel>
          </Collapse>)}
      </Card>
    </div>
  );
};

export default ManualBookingForm;

//{(bookingSelectionOption && !isEmpty(bookingSelectionOption)) && (
